/**
 * MKS-134 — Filters popover for the project board header (KanbanBoardPage).
 *
 * PURELY presentational: no data hooks (no query hooks, no milestone hooks,
 * no auth hooks) — every option list arrives as a prop, so the page stays
 * the single owner of filter state and this component tests without a
 * QueryClientProvider.
 *
 * Six controls: assignee (kind-aware composite keys — external options are
 * suffixed ' (external)', which is how the MKS-134 bugfix becomes visible),
 * MKS-510 creator (the same kind-aware keys, rendered only when the page
 * passes `creators` — its release flag's off state),
 * status (board column incl. Triage), milestone (incl. 'No milestone'),
 * MKS-164 labels (multi-select, ORed together — the ticket's own panel row,
 * unlike the standalone priority pills below), and two before/on/after
 * day-precision date filters (created, last update).
 *
 * Popover mechanics copied from TaskStatusPicker (outside mousedown + Escape
 * close, gated on `open`).
 *
 * MKS-163 follow-up — priority is NOT a control in this panel. It briefly
 * lived here as a sixth row, then moved to standalone always-visible pills in
 * the KanbanBoardPage toolbar (`PriorityFilterToggle`, mounted next to the
 * "Filters" trigger) per user feedback: "one-click priority filtering without
 * opening the panel." `value.priority` still lives in the shared
 * `BoardFilterState` and this panel's "Clear filters" still resets it (see
 * the `activeCount` note below) — only the EDITING control moved out.
 */
import { useEffect, useRef, useState } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { EMPTY_BOARD_FILTERS, countActiveBoardFilters } from '../../utils/boardFilters';
import type {
  BoardAssigneeOption,
  BoardCreatorOption,
  BoardFilterState,
  BoardLabelOption,
  DateFilter,
  DateFilterMode,
  DueFilterMode,
} from '../../utils/boardFilters';
import { chipTextColor } from '../../utils/format';
import { DateField } from '../ui/DateField';

export interface BoardStatusOption {
  key: string;
  name: string;
  color: string | null;
}

export interface BoardFilterBarProps {
  value: BoardFilterState;
  onChange: (next: BoardFilterState) => void;
  assignees: BoardAssigneeOption[];
  statuses: BoardStatusOption[];
  milestones: Array<{ id: number; name: string }>;
  /** MKS-164 — one option per label present on the loaded board (collectBoardLabels). */
  labels: BoardLabelOption[];
  /**
   * MKS-419 — merged parent + subprojects board only: the "Project" select's
   * options (`p:<id>` keys — the parent itself, then each subproject). Absent
   * or empty: no Project control, and the bar renders exactly as before.
   */
  projects?: Array<{ key: string; label: string }>;
  /**
   * MKS-510 — the "Creator" select's options (collectBoardCreators). ABSENT
   * (not empty) means no Creator control at all, which is how the page keeps
   * the release flag's off state identical to today's panel. An empty array
   * still renders the row — a board whose every column is empty has no
   * creators to offer, and hiding the control there would make it flicker.
   */
  creators?: BoardCreatorOption[];
  shown: number;
  total: number;
}

const CONTROL_CLASS =
  'w-full rounded-md border border-ms-border bg-ms-surface px-2 py-1 text-[12px] ' +
  'text-ms-text focus:outline-none focus:ring-1 focus:ring-ms-orange/40';

const LABEL_CLASS = 'block text-[11px] font-medium text-ms-text-secondary mb-1';

interface DateFilterRowProps {
  label: string;
  testIdPrefix: string;
  value: DateFilter | null;
  onChange: (next: DateFilter | null) => void;
}

/**
 * One before/on/after mode select + a date input. Declared once so the
 * created and last-update rows share the exact same logic: a blank date
 * clears the whole slot; an untouched mode defaults to 'on'; changing the
 * mode preserves an already-picked date.
 */
function DateFilterRow({ label, testIdPrefix, value, onChange }: DateFilterRowProps) {
  return (
    <div>
      <span className={LABEL_CLASS}>{label}</span>
      <div className="flex gap-2">
        <select
          data-testid={`${testIdPrefix}-mode`}
          aria-label={`${label} filter mode`}
          className={CONTROL_CLASS}
          value={value?.mode ?? 'on'}
          onChange={(e) =>
            onChange({ mode: e.target.value as DateFilterMode, date: value?.date ?? '' })
          }
        >
          <option value="before">Before</option>
          <option value="on">On</option>
          <option value="after">After</option>
        </select>
        <DateField
          data-testid={`${testIdPrefix}-date`}
          aria-label={label}
          className={CONTROL_CLASS}
          value={value?.date ?? ''}
          onChange={(date) => {
            onChange(date === '' ? null : { mode: value?.mode ?? 'on', date });
          }}
        />
      </div>
    </div>
  );
}

export function BoardFilterBar({
  value,
  onChange,
  assignees,
  statuses,
  milestones,
  labels,
  projects,
  creators,
  shown,
  total,
}: BoardFilterBarProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Esc (mirrors TaskStatusPicker).
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // MKS-163 follow-up (D-11) — deliberately UNCHANGED: this count still
  // includes `value.priority`, even though priority is no longer editable
  // from this panel. Splitting it into a "panel-only" count (to stop the
  // Filters trigger lighting up for a filter it no longer displays) would
  // require decoupling the shown/total badge's visibility from the trigger's
  // own highlight — not trivial, and the standalone pills already carry
  // their own independent pressed/active styling for at-a-glance feedback.
  // Net effect: "Filters" continues to mean "how many dimensions are
  // currently narrowing the board", regardless of which control drives which
  // dimension; "Clear filters" (below) still resets priority to `[]`.
  const activeCount = countActiveBoardFilters(value);

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        data-testid="board-filter-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Board filters"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md border ${
          activeCount > 0
            ? 'border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark font-semibold'
            : 'border-ms-border bg-ms-surface text-ms-text-secondary hover:bg-ms-canvas'
        }`}
      >
        <FunnelIcon aria-hidden="true" className="h-3.5 w-3.5 opacity-70" />
        <span>Filters</span>
        {activeCount > 0 && (
          <>
            <span
              data-testid="board-filter-count"
              className="inline-flex items-center justify-center rounded-full bg-ms-orange/15 px-1.5 text-[11px] font-semibold tabular-nums"
            >
              {activeCount}
            </span>
            <span data-testid="board-filter-counts" className="whitespace-nowrap tabular-nums">
              {shown} / {total}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Board filters"
          data-testid="board-filter-panel"
          className="absolute right-0 mt-1 w-80 rounded-lg border border-ms-border bg-white shadow-lg z-50 p-3 space-y-3"
        >
          {projects != null && projects.length > 0 && (
            <div>
              <span className={LABEL_CLASS}>Project</span>
              <select
                data-testid="board-filter-project"
                aria-label="Filter board by project"
                className={CONTROL_CLASS}
                value={value.subproject ?? ''}
                onChange={(e) =>
                  onChange({ ...value, subproject: e.target.value === '' ? null : e.target.value })
                }
              >
                <option value="">All projects</option>
                {projects.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <span className={LABEL_CLASS}>Assignee</span>
            <select
              data-testid="board-filter-assignee"
              aria-label="Filter board by assignee"
              className={CONTROL_CLASS}
              value={value.assignee ?? ''}
              onChange={(e) =>
                onChange({ ...value, assignee: e.target.value === '' ? null : e.target.value })
              }
            >
              <option value="">All assignees</option>
              {assignees.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {/* The ' (external)' suffix is the visible half of the MKS-134
                      bugfix — external assignees must be distinguishable. */}
                  {opt.kind === 'external_user' ? `${opt.name} (external)` : opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* MKS-510 — Creator row, behind `mks_510_board_creator_filter`
              (the page passes `creators` only while the flag is on). Sits
              next to Assignee because it is the same dual-kind attribution:
              the same composite keys, the same ' (external)' suffix, and its
              own option for cards with no creator stamp. */}
          {creators != null && (
            <div>
              <span className={LABEL_CLASS}>Creator</span>
              <select
                data-testid="board-filter-creator"
                aria-label="Filter board by creator"
                className={CONTROL_CLASS}
                value={value.creator ?? ''}
                onChange={(e) =>
                  onChange({ ...value, creator: e.target.value === '' ? null : e.target.value })
                }
              >
                <option value="">All creators</option>
                {creators.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {/* Same suffix as the assignee options above: two people
                        can share a name, so the external ones must read as
                        external rather than as a duplicate. */}
                    {opt.kind === 'external_user' ? `${opt.name} (external)` : opt.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <span className={LABEL_CLASS}>Status</span>
            <select
              data-testid="board-filter-status"
              aria-label="Filter board by status"
              className={CONTROL_CLASS}
              value={value.status ?? ''}
              onChange={(e) =>
                onChange({ ...value, status: e.target.value === '' ? null : e.target.value })
              }
            >
              <option value="">All statuses</option>
              {statuses.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={LABEL_CLASS}>Milestone</span>
            <select
              data-testid="board-filter-milestone"
              aria-label="Filter board by milestone"
              className={CONTROL_CLASS}
              value={value.milestone ?? ''}
              onChange={(e) =>
                onChange({ ...value, milestone: e.target.value === '' ? null : e.target.value })
              }
            >
              <option value="">All milestones</option>
              <option value="none">No milestone</option>
              {milestones.map((m) => (
                <option key={m.id} value={`m:${m.id}`}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* MKS-341 — the Due slot. A single-value <select> like Milestone
              above rather than a date pair like Created/Updated: the question
              a board reader has is "how urgent", not "which exact day", and
              the three modes are not a meaningful union (there is no reader
              whose question is "overdue OR undated"). */}
          <div>
            <span className={LABEL_CLASS}>Due</span>
            <select
              data-testid="board-filter-due"
              aria-label="Filter board by due date"
              className={CONTROL_CLASS}
              value={value.due ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  due: e.target.value === '' ? null : (e.target.value as DueFilterMode),
                })
              }
            >
              <option value="">Any due date</option>
              <option value="overdue">Overdue</option>
              <option value="this_week">This week</option>
              <option value="none">No due date</option>
            </select>
          </div>

          {/* MKS-164 — Labels row. Unlike the single-value <select> rows
              above, this is a multi-select: each option toggles independently
              and the selection ORs together (taskMatchesBoardFilters).
              Counts as ONE active slot regardless of how many are picked
              (countActiveBoardFilters). */}
          {labels.length > 0 && (
            <div>
              <span className={LABEL_CLASS}>Labels</span>
              <div className="flex flex-wrap gap-1" role="group" aria-label="Filter board by labels">
                {labels.map((label) => {
                  const selected = value.labels.includes(label.id);
                  return (
                    <button
                      key={label.id}
                      type="button"
                      data-testid={`board-filter-label-${label.id}`}
                      aria-pressed={selected}
                      onClick={() =>
                        onChange({
                          ...value,
                          labels: selected
                            ? value.labels.filter((id) => id !== label.id)
                            : [...value.labels, label.id],
                        })
                      }
                      style={
                        selected
                          ? { backgroundColor: label.color, color: chipTextColor(label.color) }
                          : undefined
                      }
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                        selected
                          ? 'border-transparent'
                          : 'border-ms-border bg-ms-surface text-ms-text-secondary hover:bg-ms-canvas'
                      }`}
                    >
                      {label.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <DateFilterRow
            label="Created date"
            testIdPrefix="board-filter-created"
            value={value.created}
            onChange={(created) => onChange({ ...value, created })}
          />

          <DateFilterRow
            label="Last update date"
            testIdPrefix="board-filter-updated"
            value={value.updated}
            onChange={(updated) => onChange({ ...value, updated })}
          />

          {activeCount > 0 && (
            <button
              type="button"
              data-testid="board-filter-clear"
              onClick={() => onChange({ ...EMPTY_BOARD_FILTERS })}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-ms-text-secondary hover:text-ms-text"
            >
              <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
